"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.rejectApplicationSchema = exports.createApplicationSchema = exports.createCandidateSchema = exports.createJobOpeningSchema = exports.jobCriteriaSetSchema = exports.jobCriterionSchema = exports.headcountPlanQuerySchema = exports.setHeadcountPlanSchema = exports.fiscalYearSchema = exports.quarterSchema = exports.QUARTERS = exports.positionListQuerySchema = exports.archivePositionSchema = exports.reassignPositionSchema = exports.changePositionStatusSchema = exports.updatePositionSchema = exports.createPositionSchema = exports.salaryBandShape = exports.headcountSchema = exports.positionStatusSchema = exports.archiveOrgUnitSchema = exports.updateTeamSchema = exports.createTeamSchema = exports.updateDepartmentSchema = exports.createDepartmentSchema = exports.updateVentureSchema = exports.createVentureSchema = exports.orgUnitKindSchema = exports.ORG_UNIT_KINDS = exports.orgScopeSchema = exports.versionedSchema = exports.twoDecimalMarkSchema = exports.listQuerySchema = exports.withdrawalReasonCodeSchema = exports.rejectionReasonCodeSchema = exports.criterionClassSchema = exports.criterionTypeSchema = exports.languageProficiencySchema = exports.educationLevelSchema = exports.workModeSchema = exports.employmentTypeSchema = exports.roleKeySchema = exports.jobOpeningStatusSchema = exports.requisitionStatusSchema = exports.ballInCourtSchema = exports.applicationStageSchema = exports.TRANSLITERATION_EQUIVALENCES = exports.phoneSchema = exports.emailSchema = exports.uuidSchema = void 0;
exports.uploadDocumentSchema = exports.ALLOWED_DOCUMENT_MIME_TYPES = exports.MAX_UPLOAD_BYTES = exports.passwordSchema = exports.MIN_PASSWORD_LENGTH = exports.reorderApplicationSchema = exports.moveApplicationStageSchema = exports.withdrawApplicationSchema = void 0;
exports.normalisePhone = normalisePhone;
exports.normaliseEmail = normaliseEmail;
exports.isNameTitle = isNameTitle;
exports.normaliseName = normaliseName;
exports.checkSalaryBand = checkSalaryBand;
const zod_1 = require("zod");
const enums_1 = require("./enums");
const money_1 = require("./money");
const reason_codes_1 = require("./reason-codes");
// One Zod schema per input, validating the request on the server (CLAUDE.md §10)
// and driving the React Hook Form resolver on the client. One definition, no drift.
//
// Messages come from §L26 — the validation and error library — so the same wording
// appears on every surface.
exports.uuidSchema = zod_1.z.uuid('That does not look like a valid record reference.');
/** §L26 field formats. */
exports.emailSchema = zod_1.z
    .string()
    .trim()
    .toLowerCase()
    .email('Enter an email address in the form name@example.com.');
/**
 * §L26 / L2: Bangladeshi mobile numbers, accepted in any common local format and
 * normalised on save. The stored form is always +880XXXXXXXXXX.
 */
exports.phoneSchema = zod_1.z
    .string()
    .trim()
    .regex(/^(?:\+?880|0)?1[3-9]\d{8}$/, 'Enter a valid mobile number.');
function normalisePhone(input) {
    const digits = input.replace(/\D/g, '');
    const withoutCountry = digits.replace(/^880/, '').replace(/^0/, '');
    return `+880${withoutCountry}`;
}
/**
 * L2 email normalisation: lowercase, and strip dots and +tags in the local part for
 * Gmail-style addresses, so one person with three spellings is still one person.
 */
function normaliseEmail(input) {
    const trimmed = input.trim().toLowerCase();
    const atIndex = trimmed.lastIndexOf('@');
    if (atIndex < 1)
        return trimmed;
    const local = trimmed.slice(0, atIndex);
    const domain = trimmed.slice(atIndex + 1);
    const GMAIL_STYLE_DOMAINS = ['gmail.com', 'googlemail.com'];
    const withoutTag = local.split('+')[0] ?? local;
    return GMAIL_STYLE_DOMAINS.includes(domain)
        ? `${withoutTag.replace(/\./g, '')}@${domain}`
        : `${withoutTag}@${domain}`;
}
/** L2 name normalisation, including the honorifics the taxonomy names explicitly. */
const HONORIFICS = [
    'md.',
    'md',
    'mohammad',
    'mohammed',
    'muhammad',
    'mst.',
    'mst',
    'mrs.',
    'mrs',
    'mr.',
    'mr',
    'ms.',
    'ms',
    'dr.',
    'dr',
];
/**
 * L2 transliteration equivalence table, seeded from the taxonomy. Maintained at
 * ADMIN and extended from real data — this table is the difference between working
 * dedupe and useless dedupe in this market.
 */
exports.TRANSLITERATION_EQUIVALENCES = [
    ['mohammad', 'muhammad', 'mohammed', 'md'],
    ['rahman', 'rahaman', 'rehman'],
    ['hossain', 'hussain', 'hosain'],
    ['chowdhury', 'choudhury', 'chaudhury'],
    ['ahmed', 'ahmad'],
    ['karim', 'kareem'],
    ['islam', 'islaam'],
];
/**
 * A word `normaliseName` drops as a title before reading the table ("Md.", "Mrs"), so a spelling of it in the table
 * never matches anything. Name matching says so on the row (Admin redesign 2026-11-02).
 */
function isNameTitle(word) {
    return HONORIFICS.includes(word.trim().toLowerCase());
}
/**
 * `equivalences` is the table as ADMIN holds it (S13); each group's first spelling is
 * the one every variant is rewritten to. The seeded list is the default.
 */
function normaliseName(input, equivalences = exports.TRANSLITERATION_EQUIVALENCES) {
    const canonical = new Map(equivalences.flatMap((group) => group.map((variant) => [variant.toLowerCase(), (group[0] ?? variant).toLowerCase()])));
    const words = input
        .toLowerCase()
        .replace(/[^\p{L}\p{N}\s.]/gu, ' ')
        .split(/\s+/)
        .filter((w) => w.length > 0 && !HONORIFICS.includes(w));
    return words.map((w) => canonical.get(w) ?? w).join(' ').trim();
}
// --- Enum schemas -----------------------------------------------------------
exports.applicationStageSchema = zod_1.z.enum(enums_1.APPLICATION_STAGES);
exports.ballInCourtSchema = zod_1.z.enum(enums_1.BALL_IN_COURT);
exports.requisitionStatusSchema = zod_1.z.enum(enums_1.REQUISITION_STATUSES);
exports.jobOpeningStatusSchema = zod_1.z.enum(enums_1.JOB_OPENING_STATUSES);
exports.roleKeySchema = zod_1.z.enum(enums_1.ROLE_KEYS);
exports.employmentTypeSchema = zod_1.z.enum(enums_1.EMPLOYMENT_TYPES);
exports.workModeSchema = zod_1.z.enum(enums_1.WORK_MODES);
exports.educationLevelSchema = zod_1.z.enum(enums_1.EDUCATION_LEVELS);
exports.languageProficiencySchema = zod_1.z.enum(enums_1.LANGUAGE_PROFICIENCIES);
exports.criterionTypeSchema = zod_1.z.enum(enums_1.CRITERION_TYPES);
exports.criterionClassSchema = zod_1.z.enum(enums_1.CRITERION_CLASSES);
exports.rejectionReasonCodeSchema = zod_1.z
    .string()
    .refine((c) => reason_codes_1.REJECTION_REASON_CODES.includes(c), 'Select a rejection reason.');
exports.withdrawalReasonCodeSchema = zod_1.z
    .string()
    .refine((c) => reason_codes_1.WITHDRAWAL_REASON_CODES.includes(c), 'Select a withdrawal reason.');
// --- Shared building blocks -------------------------------------------------
/** Every list endpoint takes the same page/sort/search envelope (06-qol §3). */
exports.listQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(200).default(50),
    sortBy: zod_1.z.string().optional(),
    sortDirection: zod_1.z.enum(['asc', 'desc']).default('desc'),
    /** 06-qol §6: minimum 2 characters on type-ahead. */
    query: zod_1.z.string().trim().min(2).optional(),
});
/**
 * A mark to two decimal places. Checked with a little give, as 2.3 * 100 is
 * 229.99999999999997 in floating point (D85); 2.345 is still refused.
 */
exports.twoDecimalMarkSchema = zod_1.z
    .number()
    .min(0, 'A mark cannot be below 0.')
    .refine((value) => Math.abs(Math.round(value * 100) - value * 100) < 1e-6, 'Use at most two decimal places.');
/** Every mutation carries the row version it read, so a stale write 409s (S7). */
exports.versionedSchema = zod_1.z.object({
    version: zod_1.z.number().int().nonnegative(),
});
// --- Organisation -----------------------------------------------------------
//
// Venture > Department > Team, three fixed levels (S5 build notes: arbitrary depth
// "is not needed and complicates every rollup query"). Positions hang off a
// department, and optionally a team inside it.
/**
 * A tree node reference. Every org-aware list takes this envelope, which is what
 * makes "click a node, filter the whole application" one mechanism rather than
 * three (S5 acceptance criterion 2).
 *
 * The narrowest field present wins — a team id makes the department and venture ids
 * redundant, and the server derives them rather than trusting what was sent.
 */
exports.orgScopeSchema = zod_1.z.object({
    ventureId: exports.uuidSchema.optional(),
    departmentId: exports.uuidSchema.optional(),
    teamId: exports.uuidSchema.optional(),
});
exports.ORG_UNIT_KINDS = ['venture', 'department', 'team'];
exports.orgUnitKindSchema = zod_1.z.enum(exports.ORG_UNIT_KINDS);
exports.createVentureSchema = zod_1.z.object({
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120),
    code: zod_1.z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(12)
        .regex(/^[A-Z0-9]+$/, 'Use letters and numbers only — the code goes into position codes.'),
    legalName: zod_1.z.string().trim().max(200).optional(),
    defaultCurrency: money_1.currencyCodeSchema.default('BDT'),
    timezone: zod_1.z.string().default('Asia/Dhaka'),
});
/** The code is left out: changing it would orphan every position code beneath it. */
exports.updateVentureSchema = exports.versionedSchema.extend({
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120).optional(),
    legalName: zod_1.z.string().trim().max(200).nullable().optional(),
    defaultCurrency: money_1.currencyCodeSchema.optional(),
    timezone: zod_1.z.string().optional(),
});
exports.createDepartmentSchema = zod_1.z.object({
    ventureId: exports.uuidSchema,
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120),
    code: zod_1.z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(12)
        .regex(/^[A-Z0-9]+$/, 'Use letters and numbers only — the code goes into position codes.'),
    headUserId: exports.uuidSchema.optional(),
});
exports.updateDepartmentSchema = exports.versionedSchema.extend({
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120).optional(),
    headUserId: exports.uuidSchema.nullable().optional(),
});
exports.createTeamSchema = zod_1.z.object({
    departmentId: exports.uuidSchema,
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120),
    leadUserId: exports.uuidSchema.optional(),
});
exports.updateTeamSchema = exports.versionedSchema.extend({
    name: zod_1.z.string().trim().min(1, 'Enter a name.').max(120).optional(),
    leadUserId: exports.uuidSchema.nullable().optional(),
});
/** F3.01: archive only, never delete. A note, because "why" outlives the person. */
exports.archiveOrgUnitSchema = exports.versionedSchema.extend({
    note: zod_1.z.string().trim().max(500).optional(),
});
// --- Positions --------------------------------------------------------------
exports.positionStatusSchema = zod_1.z.enum(enums_1.POSITION_STATUSES);
/** §L26: "Headcount must be between 1 and 50." */
exports.headcountSchema = zod_1.z
    .number()
    .int()
    .min(1, 'Headcount must be between 1 and 50.')
    .max(50, 'Headcount must be between 1 and 50.');
exports.salaryBandShape = {
    salaryBandMinMinor: money_1.minorUnitsSchema.optional(),
    salaryBandMaxMinor: money_1.minorUnitsSchema.optional(),
    salaryCurrency: money_1.currencyCodeSchema.optional(),
};
/**
 * The band rules, stated once and applied on both create and edit.
 *
 * A band with no currency is precisely the bug non-negotiable #7 exists to prevent:
 * an amount whose unit nobody recorded. D13 puts the currency on the Position and
 * offers inherit it from there, so it cannot be left to be guessed later.
 */
function checkSalaryBand(value, ctx) {
    const { salaryBandMinMinor: min, salaryBandMaxMinor: max, salaryCurrency } = value;
    if (min !== undefined && max !== undefined && min > max) {
        ctx.addIssue({
            code: 'custom',
            message: 'The minimum salary cannot be above the maximum.',
            path: ['salaryBandMinMinor'],
        });
    }
    if ((min !== undefined || max !== undefined) && salaryCurrency === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Choose a currency for the salary band.',
            path: ['salaryCurrency'],
        });
    }
}
/**
 * F3.02 — create a position.
 *
 * There is no `code` field: the server generates `{VENTURE}-{DEPT}-{SEQ}` (S5 build
 * notes), which is the only way the sequence stays unique per unit under two people
 * creating at once. There is no `ventureId` either — it is the department's venture
 * by definition, and accepting it separately only invites the two to disagree.
 */
exports.createPositionSchema = zod_1.z
    .object({
    departmentId: exports.uuidSchema,
    teamId: exports.uuidSchema.optional(),
    title: zod_1.z.string().trim().min(1, 'Enter a job title.').max(160),
    grade: zod_1.z.string().trim().max(32).optional(),
    employmentType: exports.employmentTypeSchema.default('full_time'),
    workMode: exports.workModeSchema.default('onsite'),
    location: zod_1.z.string().trim().max(160).optional(),
    totalSlots: exports.headcountSchema.default(1),
    /** F3.05 — a seat may be born planned rather than budgeted. */
    status: zod_1.z.enum(['planned', 'vacant']).default('vacant'),
    reportsToPositionId: exports.uuidSchema.optional(),
    hiringManagerUserId: exports.uuidSchema.optional(),
    ...exports.salaryBandShape,
})
    .superRefine(checkSalaryBand);
/**
 * F3.03 — edit a position.
 *
 * "Band change requires reason" (F3.03), so the reason is demanded whenever any of
 * the three band fields is present. It is recorded on the event rather than in a
 * column, because the question it answers is always "why did it change *then*".
 */
exports.updatePositionSchema = exports.versionedSchema
    .extend({
    title: zod_1.z.string().trim().min(1, 'Enter a job title.').max(160).optional(),
    grade: zod_1.z.string().trim().max(32).nullable().optional(),
    employmentType: exports.employmentTypeSchema.optional(),
    workMode: exports.workModeSchema.optional(),
    location: zod_1.z.string().trim().max(160).nullable().optional(),
    totalSlots: exports.headcountSchema.optional(),
    reportsToPositionId: exports.uuidSchema.nullable().optional(),
    hiringManagerUserId: exports.uuidSchema.nullable().optional(),
    ...exports.salaryBandShape,
    bandChangeReason: zod_1.z.string().trim().min(1).max(500).optional(),
})
    .superRefine((value, ctx) => {
    checkSalaryBand(value, ctx);
    const touchesBand = value.salaryBandMinMinor !== undefined ||
        value.salaryBandMaxMinor !== undefined ||
        value.salaryCurrency !== undefined;
    if (touchesBand && !value.bandChangeReason) {
        ctx.addIssue({
            code: 'custom',
            message: 'Say why the salary band is changing.',
            path: ['bandChangeReason'],
        });
    }
});
/** F3.04 / F3.05 and the S24 hooks — one endpoint for every status move. */
exports.changePositionStatusSchema = exports.versionedSchema.extend({
    status: exports.positionStatusSchema,
    /** L7: a budget freeze requires one. The state machine decides when. */
    reason: zod_1.z.string().trim().max(500).optional(),
});
/**
 * F3.06 — move a seat to another unit. Logged, and historical reporting keeps the
 * original unit for free: a report reads the event, never the current row.
 */
exports.reassignPositionSchema = exports.versionedSchema.extend({
    departmentId: exports.uuidSchema,
    teamId: exports.uuidSchema.nullable().optional(),
    note: zod_1.z.string().trim().max(500).optional(),
});
/** F3.07 — archive. Never a delete: the ledger loses it, every report keeps it. */
exports.archivePositionSchema = exports.versionedSchema.extend({
    note: zod_1.z.string().trim().max(500).optional(),
});
exports.positionListQuerySchema = exports.listQuerySchema.extend({
    ...exports.orgScopeSchema.shape,
    /** One or more, comma-separated in the query string: `?status=planned,vacant`. */
    status: zod_1.z
        .string()
        .transform((value) => value.split(','))
        .pipe(zod_1.z.array(exports.positionStatusSchema).min(1))
        .optional(),
    /** Off by default — an archived seat is off the ledger, not out of the system. */
    includeArchived: zod_1.z.stringbool().default(false),
    /** Only the archived seats, for reviewing or restoring them. Wins over `includeArchived`. */
    archivedOnly: zod_1.z.stringbool().default(false),
    /**
     * Workflow review 2026-10-29: only seats a hire can be requested for now — none with a
     * request already open (a position has one at a time), so the picker never offers a
     * seat the server would refuse.
     */
    requestable: zod_1.z.stringbool().default(false),
});
// --- Headcount plans --------------------------------------------------------
exports.QUARTERS = [1, 2, 3, 4];
/**
 * F3.08 — per org unit per quarter.
 *
 * A year and a quarter number rather than a date range, because open question O2
 * (fiscal year start month) is unanswered. Storing the pair means only the *label*
 * changes once it is answered — "Q2 2026" either starts in April or in July, and no
 * stored row has to move either way.
 */
exports.quarterSchema = zod_1.z
    .number()
    .int()
    .min(1, 'Pick a quarter between 1 and 4.')
    .max(4, 'Pick a quarter between 1 and 4.');
exports.fiscalYearSchema = zod_1.z
    .number()
    .int()
    .min(2020, 'Pick a year between 2020 and 2100.')
    .max(2100, 'Pick a year between 2020 and 2100.');
exports.setHeadcountPlanSchema = zod_1.z
    .object({
    ventureId: exports.uuidSchema.optional(),
    departmentId: exports.uuidSchema.optional(),
    teamId: exports.uuidSchema.optional(),
    fiscalYear: exports.fiscalYearSchema,
    quarter: exports.quarterSchema,
    plannedHeadcount: zod_1.z.number().int().min(0).max(9999).default(0),
    approvedHeadcount: zod_1.z.number().int().min(0).max(9999).default(0),
    budgetAmountMinor: money_1.minorUnitsSchema.optional(),
    budgetCurrency: money_1.currencyCodeSchema.optional(),
    notes: zod_1.z.string().trim().max(1000).optional(),
})
    .superRefine((value, ctx) => {
    if (!value.ventureId && !value.departmentId && !value.teamId) {
        ctx.addIssue({
            code: 'custom',
            message: 'Choose the unit this plan is for.',
            path: ['departmentId'],
        });
    }
    // Approving more seats than were planned is a data-entry slip, not a plan.
    if (value.approvedHeadcount > value.plannedHeadcount) {
        ctx.addIssue({
            code: 'custom',
            message: 'Approved headcount cannot be more than planned.',
            path: ['approvedHeadcount'],
        });
    }
    if (value.budgetAmountMinor !== undefined && value.budgetCurrency === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Choose a currency for the budget.',
            path: ['budgetCurrency'],
        });
    }
});
exports.headcountPlanQuerySchema = exports.orgScopeSchema.extend({
    fiscalYear: zod_1.z.coerce.number().int().min(2020).max(2100).optional(),
});
// --- Demand: see requisitions.ts ------------------------------------------
// --- Job configuration ------------------------------------------------------
exports.jobCriterionSchema = zod_1.z.object({
    type: exports.criterionTypeSchema,
    label: zod_1.z.string().trim().min(1, 'Name this criterion.').max(120),
    class: exports.criterionClassSchema,
    weight: zod_1.z.number().int().min(0).max(100),
    params: zod_1.z.record(zod_1.z.string(), zod_1.z.unknown()).optional(),
});
/** L1: all weights across a job must total 100. S11 shows this as a live running total. */
exports.jobCriteriaSetSchema = zod_1.z
    .array(exports.jobCriterionSchema)
    .refine((list) => list.reduce((sum, c) => sum + c.weight, 0) === 100, {
    message: 'The criteria weights must add up to exactly 100.',
});
exports.createJobOpeningSchema = zod_1.z.object({
    ventureId: exports.uuidSchema,
    departmentId: exports.uuidSchema,
    requisitionId: exports.uuidSchema.optional(),
    positionId: exports.uuidSchema.optional(),
    roleTemplateId: exports.uuidSchema.optional(),
    title: zod_1.z.string().trim().min(1, 'Enter a job title.').max(160),
    description: zod_1.z.string().trim().max(20000).optional(),
    responsibilities: zod_1.z.string().trim().max(20000).optional(),
    requirements: zod_1.z.string().trim().max(20000).optional(),
    employmentType: exports.employmentTypeSchema.default('full_time'),
    workMode: exports.workModeSchema.default('onsite'),
    location: zod_1.z.string().trim().max(160).optional(),
    /** D14: display only, never used in any calculation. */
    shiftHours: zod_1.z.string().trim().max(120).optional(),
    slotsTotal: zod_1.z.number().int().positive().max(100).default(1),
    isSalaryPublic: zod_1.z.boolean().default(false),
});
// --- Supply -----------------------------------------------------------------
exports.createCandidateSchema = zod_1.z
    .object({
    // UTF-8 throughout — Bangla script names must round-trip exactly (D3).
    fullName: zod_1.z.string().trim().min(1, 'Enter the candidate name.').max(160),
    email: exports.emailSchema.optional(),
    phone: exports.phoneSchema.optional(),
    nationalId: zod_1.z.string().trim().max(32).optional(),
    dateOfBirth: zod_1.z.coerce.date().optional(),
    city: zod_1.z.string().trim().max(80).optional(),
    country: zod_1.z.string().trim().length(2).default('BD'),
    linkedinUrl: zod_1.z.url('Enter a full web address starting with https://').optional(),
    currentEmployer: zod_1.z.string().trim().max(160).optional(),
    currentTitle: zod_1.z.string().trim().max(160).optional(),
    totalExperienceMonths: zod_1.z.number().int().min(0).max(720).optional(),
    highestEducation: exports.educationLevelSchema.optional(),
    expectedSalaryMinor: zod_1.z.bigint().nonnegative().optional(),
    expectedSalaryCurrency: money_1.currencyCodeSchema.optional(),
    noticePeriodDays: zod_1.z.number().int().min(0).max(365).optional(),
    isWillingToRelocate: zod_1.z.boolean().optional(),
})
    .refine((v) => v.email !== undefined || v.phone !== undefined, {
    message: 'Enter at least an email address or a phone number.',
    path: ['email'],
});
exports.createApplicationSchema = zod_1.z.object({
    candidateId: exports.uuidSchema,
    jobOpeningId: exports.uuidSchema,
    postingId: exports.uuidSchema.optional(),
    source: zod_1.z.string().trim().max(80).optional(),
    answers: zod_1.z
        .array(zod_1.z.object({
        questionId: exports.uuidSchema,
        value: zod_1.z.unknown(),
    }))
        .default([]),
});
/**
 * Non-negotiable #2: the reject action stays disabled until a reason is selected,
 * and the server refuses the request without one — the UI is not the enforcement.
 */
exports.rejectApplicationSchema = exports.versionedSchema.extend({
    applicationId: exports.uuidSchema,
    reasonCode: exports.rejectionReasonCodeSchema,
    note: zod_1.z.string().trim().max(2000).optional(),
    addToPoolId: exports.uuidSchema.optional(),
});
exports.withdrawApplicationSchema = exports.versionedSchema.extend({
    applicationId: exports.uuidSchema,
    reasonCode: exports.withdrawalReasonCodeSchema,
    note: zod_1.z.string().trim().max(2000).optional(),
});
/** A plain stage move. Rejections and withdrawals use their own schemas above. */
exports.moveApplicationStageSchema = exports.versionedSchema.extend({
    applicationId: exports.uuidSchema,
    toStage: exports.applicationStageSchema,
    /** §L23: skipping a stage is HR-Lead-only and the reason is mandatory. */
    reason: zod_1.z.string().trim().max(2000).optional(),
});
/** ADR 0002: a card dropped on the board must stay where it was dropped. */
exports.reorderApplicationSchema = exports.versionedSchema.extend({
    applicationId: exports.uuidSchema,
    toStage: exports.applicationStageSchema,
    afterApplicationId: exports.uuidSchema.nullable(),
    beforeApplicationId: exports.uuidSchema.nullable(),
});
// --- Identity ---------------------------------------------------------------
/**
 * F1.04: ten characters; length beats composition rules. The one minimum, read by
 * the API's validation and by every hint on screen, so the two cannot disagree. The
 * product owner settled ten over the twelve S1 shipped (D18).
 */
exports.MIN_PASSWORD_LENGTH = 10;
/** §L26: state the actual limit, never "password too weak". */
exports.passwordSchema = zod_1.z
    .string()
    .min(exports.MIN_PASSWORD_LENGTH, `Use at least ${exports.MIN_PASSWORD_LENGTH} characters.`)
    .max(128, 'Use at most 128 characters.');
// `loginSchema` and `inviteUserSchema` moved to `auth-schemas.ts` in S3/S4. The
// stubs here predated scope, and an invite without a scope is not a permission.
// --- File rules (§L26) ------------------------------------------------------
exports.MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
exports.ALLOWED_DOCUMENT_MIME_TYPES = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/png',
];
exports.uploadDocumentSchema = zod_1.z.object({
    fileName: zod_1.z.string().trim().min(1).max(255),
    mimeType: zod_1.z.enum(exports.ALLOWED_DOCUMENT_MIME_TYPES),
    byteSize: zod_1.z
        .number()
        .int()
        .positive()
        .max(exports.MAX_UPLOAD_BYTES, 'Maximum 10 MB.'),
    documentType: zod_1.z.string().trim().min(1).max(60),
});
//# sourceMappingURL=schemas.js.map